import React, { RefObject, useContext, useEffect, useRef, useState } from 'react';

import { PawCircle, CheckCircleFilled } from '@zooplus/icons';
import { CartButton } from '@zooplus/ui-components';

import zooclubPoints from '../../../common/assets/img/zooclubPoints.svg';
import CartEventsContext from '../../../common/contexts/CartEventsContext';
import LocResourcesContext from '../../../common/contexts/LocResourcesContext';
import { RewardShopArticle } from '../../../common/utils/types';

const GLOBAL_PREFIX = 'loy-bp-rws-donations';

interface DonationArticleProps {
  article: RewardShopArticle;
  quantityInCart: number;
  isLoyaltyProgramEnabled: boolean;
}

const DonationArticle = ({
  article,
  quantityInCart,
  isLoyaltyProgramEnabled,
}: DonationArticleProps): JSX.Element => {
  const { onAddToCart, isLogged, redeemableBalance } = useContext(CartEventsContext);
  const { onFormatLocale } = useContext(LocResourcesContext);
  const [addedState, setAddedState] = useState<string>('reset');
  const buttonRef: RefObject<HTMLButtonElement> = useRef<HTMLButtonElement>(null);
  const prefix = isLogged ? '/account/bonus' : '/bonus';
  const path = prefix + '/donations/' + article.shopIdentifier;

  const addToCart = () => {
    setAddedState('adding');
    const node = buttonRef.current;
    const quantity = quantityInCart + 1;
    onAddToCart?.(article.articleId, quantity, node);
  };

  useEffect(() => {
    if (addedState === 'adding') {
      setAddedState('added');
      setTimeout(() => {
        setAddedState('reset');
      }, 5000);
    }
  }, [quantityInCart]);

  const canIncreaseQuantity = () => {
    return (article.quantityInCart ?? 0) < article.selection[article.selection.length - 1];
  };

  return (
    <li className={`${GLOBAL_PREFIX}__articles__card js-addToCart`}>
      <div className={`${GLOBAL_PREFIX}__articles__card__icon--paw`}>
        {isLoyaltyProgramEnabled ? <img src={zooclubPoints} alt="" /> : <PawCircle />}
      </div>
      <a className={`${GLOBAL_PREFIX}__articles__card--content`} href={path}>
        <span className={`${GLOBAL_PREFIX}__articles__card--content-subtitle`}>
          {onFormatLocale?.('loy.rewards.shop.donate.subtitle')}
        </span>
        <div className={`${GLOBAL_PREFIX}__articles__card--content-points`}>
          {article.points} {onFormatLocale?.('bp.points')}
        </div>
      </a>

      {isLogged &&
      redeemableBalance &&
      article.points <= redeemableBalance &&
      canIncreaseQuantity() ? (
        <div className="addtocart">
          {addedState !== 'added' && addedState === 'reset' && (
            <CartButton
              iconOnly
              ref={buttonRef}
              size="small"
              title="Add to cart"
              data-zta={`addtocartbtn_${article.shopIdentifier}`}
              onClick={() => addToCart()}
            />
          )}

          {addedState !== 'added' && addedState === 'adding' && (
            <CartButton iconOnly isLoading loadingIconType="paw" size="small" />
          )}
          {addedState === 'added' && (
            <div
              aria-label="Product added to the cart"
              title="Product added to the cart"
              data-zta={`addtocartbtn_${article.shopIdentifier}`}
              className={`${GLOBAL_PREFIX}__articles__card__icon--check`}>
              <CheckCircleFilled />
            </div>
          )}
        </div>
      ) : null}
    </li>
  );
};

export default DonationArticle;
